import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Overline } from "./Reveal";
import { FullLogo } from "./Logo";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-36 sm:pt-44 pb-24 sm:pb-32 overflow-hidden"
    >
      {/* Soft ambient glows */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute top-[-10%] right-[-10%] w-[55vw] h-[55vw] rounded-full"
          style={{ background: "radial-gradient(circle at 30% 30%, #E6ECFA 0%, transparent 60%)" }}
        />
        <div
          className="absolute bottom-[-20%] left-[-10%] w-[40vw] h-[40vw] rounded-full"
          style={{ background: "radial-gradient(circle at 60% 60%, #F4F0E6 0%, transparent 65%)" }}
        />
      </div>

      <div className="max-page container-px grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <motion.div variants={item}>
            <Overline>One Sphere · General Trading</Overline>
          </motion.div>

          <motion.h1
            variants={item}
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[1.02] tracking-tight text-foreground"
          >
            One partner.
            <br />
            <span className="italic text-primary/90">Every category.</span>
            <br />
            <span className="whitespace-nowrap">
              One{" "}
              <span className="relative inline-block">
                Sphere
                <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-primary/70 rounded-full" />
              </span>
              .
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            A unified trading house sourcing building materials, interiors,
            IT systems, HVAC components, fitness equipment and bespoke business
            solutions — engineered for the standards your enterprise expects.
          </motion.p>

          <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full text-sm tracking-wide hover:bg-foreground transition-all duration-500"
            >
              Request a Quote
              <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm tracking-wide text-foreground border border-border hover:border-primary hover:text-primary transition-all duration-500"
            >
              Explore Capabilities
            </Link>
          </motion.div>

          <motion.div variants={item} className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "09", v: "Specialty Verticals" },
              { k: "120+", v: "Trusted Partners" },
              { k: "24/7", v: "Procurement Desk" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-3xl sm:text-4xl text-foreground">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Brand mark — actual One Sphere logo (transparent PNG). */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative flex justify-center"
        >
          <div className="relative w-full max-w-[720px] aspect-square">
            {/* Soft halo */}
            <div className="absolute inset-10 rounded-full bg-primary/[0.05] blur-3xl" />
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 35% 30%, rgba(30,58,138,0.07) 0%, transparent 65%)",
              }}
            />
            {/* The logo */}
            <div className="relative z-10 w-full h-full flex items-center justify-center float-slow">
              <FullLogo width="100%" className="w-full" />
            </div>
            {/* Shadow under */}
            <div className="absolute left-[15%] right-[15%] bottom-2 h-10 bg-primary/15 blur-2xl rounded-full" />
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.35em] text-muted-foreground uppercase">
            Unified · Trading · Sourcing
          </div>
        </motion.div>
      </div>

      {/* Trust marquee */}
      <div className="relative mt-28 sm:mt-36 hairline pt-10 overflow-hidden">
        <div className="container-px max-page text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
          Serving partners across
        </div>
        <div className="overflow-hidden">
          <div className="marquee whitespace-nowrap flex gap-16 text-foreground/40 font-display text-2xl sm:text-3xl">
            {[
              ...("CONSTRUCTION · INTERIORS · IT & SYSTEMS · HVAC · FITNESS · LOGISTICS · BROKERAGE · EVENTS · GIFTING").split(" · "),
              ...("CONSTRUCTION · INTERIORS · IT & SYSTEMS · HVAC · FITNESS · LOGISTICS · BROKERAGE · EVENTS · GIFTING").split(" · "),
            ].map((w, i) => (
              <span key={i} className="inline-flex items-center gap-16">
                {w}
                <span className="w-2 h-2 rounded-full bg-primary/40" />
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
